/**
 * Monitoraggio atleti — funzioni pure per la maschera `/monitoring`.
 *
 * Il calcolo vive sul server (`backend/services/monitoring.py`); qui ci sono
 * solo le regole di presentazione: periodi, etichette di stato, testo delle
 * segnalazioni (il server manda codici e numeri, mai frasi), formato delle
 * quantita' con la loro unita'. Niente React, niente fetch, cosi' si provano
 * con Jest. Spec: docs/monitoring/ANALYSIS.md.
 */
import { mesocyclesForRange, primaryMesocycle } from "./seasonAgenda";

export const PERIOD_PRESETS = ["7d", "14d", "28d", "this_week", "last_week"];

const DAY_MS = 86400000;

const toDate = (iso) => new Date(`${iso}T12:00:00Z`);
const toIso = (date) => date.toISOString().slice(0, 10);

export function shiftIsoDate(iso, days) {
	return toIso(new Date(toDate(iso).getTime() + days * DAY_MS));
}

export function mondayOf(iso) {
	const date = toDate(iso);
	return shiftIsoDate(iso, -((date.getUTCDay() + 6) % 7));
}

/**
 * Il periodo di un preset. «Settimana» e' quella di calendario (lunedi-
 * domenica), gli «ultimi N giorni» finiscono oggi: le due cose non si
 * confondono, come chiede il brief.
 */
export function periodRange(preset, todayIso) {
	switch (preset) {
		case "7d":
			return { from: shiftIsoDate(todayIso, -6), to: todayIso };
		case "28d":
			return { from: shiftIsoDate(todayIso, -27), to: todayIso };
		case "this_week": {
			const monday = mondayOf(todayIso);
			return { from: monday, to: shiftIsoDate(monday, 6) };
		}
		case "last_week": {
			const monday = shiftIsoDate(mondayOf(todayIso), -7);
			return { from: monday, to: shiftIsoDate(monday, 6) };
		}
		case "14d":
		default:
			return { from: shiftIsoDate(todayIso, -13), to: todayIso };
	}
}

/** Stato di una giornata/cella → tono e chiave di traduzione. Il colore non viaggia mai da solo. */
export const DAY_STATUS = {
	complete: { tone: "volt", labelKey: "monStatusComplete" },
	partial: { tone: "amber", labelKey: "monStatusPartial" },
	none: { tone: "blaze", labelKey: "monStatusNone" },
	upcoming: { tone: "muted", labelKey: "monStatusUpcoming" },
	rest: { tone: "muted", labelKey: "monStatusRest" },
};

export function dayStatusMeta(status) {
	return DAY_STATUS[status] || DAY_STATUS.rest;
}

/** «3 complete · 1 parziale · 1 senza esito su 5 assegnate»: mai una percentuale ambigua. */
export function coverageSentence(row, t) {
	const assigned = Number(row?.assigned_past || 0);
	if (!assigned) return t("monNoAssignedDays");
	const parts = [];
	if (row.complete) parts.push(`${row.complete} ${t("monCompleteShort")}`);
	if (row.partial) parts.push(`${row.partial} ${t("monPartialShort")}`);
	if (row.missing) parts.push(`${row.missing} ${t("monMissingShort")}`);
	return `${parts.join(" · ")} ${t("monOutOf")} ${assigned} ${t("monAssignedShort")}`;
}

export function coverageRatio(row) {
	const assigned = Number(row?.assigned_past || 0);
	if (!assigned) return null;
	return (Number(row.complete || 0) + Number(row.partial || 0)) / assigned;
}

export function daysSinceLabel(row, t) {
	if (!row?.last_activity_date) return t("monNoActivityLookback");
	const days = row.days_since_activity;
	if (days === 0) return t("today");
	if (days === 1) return t("yesterday");
	return `${days} ${t("monDaysAgo")}`;
}

const WELLNESS_FIELD_KEYS = {
	sleep_quality: "monWellnessSleep",
	energy: "monWellnessEnergy",
	mood: "monWellnessMood",
	soreness: "monWellnessSoreness",
	stress: "monWellnessStress",
};

/** Il testo di una segnalazione dal suo codice e dai suoi numeri: motivo e dati, mai un semaforo. */
export function flagLabel(flag, t, language = "it") {
	const data = flag?.data || {};
	switch (flag?.code) {
		case "missing_logs":
			return `${data.missing} ${t("monFlagMissingLogs")} ${data.assigned}`;
		case "inactive":
			return data.days === null || data.days === undefined
				? `${t("monFlagInactiveOver")} ${data.lookback} ${t("monDaysWord")}`
				: `${t("monFlagInactive")} ${data.days} ${t("monDaysWord")}`;
		case "high_rpe":
			return `${data.count} ${t("monFlagHighRpe")} ${data.threshold}`;
		case "wellness_low": {
			const fields = (data.fields || [])
				.map((hit) => `${t(WELLNESS_FIELD_KEYS[hit.field] || hit.field)} ${hit.value}/5`)
				.join(", ");
			return `${t("monFlagWellnessLow")} ${formatShortDay(data.date, language)}: ${fields}`;
		}
		case "wellness_gap":
			return t("monFlagWellnessGap");
		case "no_data":
			return t("monFlagNoData");
		default:
			return flag?.code || "";
	}
}

export function formatShortDay(iso, language = "it") {
	if (!iso) return "";
	return toDate(iso).toLocaleDateString(language === "en" ? "en-GB" : "it-IT", {
		day: "2-digit",
		month: "2-digit",
		timeZone: "UTC",
	});
}

export function weekdayShort(iso, language = "it") {
	if (!iso) return "";
	return toDate(iso)
		.toLocaleDateString(language === "en" ? "en-GB" : "it-IT", { weekday: "short", timeZone: "UTC" })
		.replace(".", "");
}

export function formatSeconds(total) {
	const seconds = Math.max(0, Math.round(Number(total) || 0));
	const minutes = Math.floor(seconds / 60);
	const rest = seconds % 60;
	if (minutes >= 60) {
		const hours = Math.floor(minutes / 60);
		return `${hours}h ${String(minutes % 60).padStart(2, "0")}'`;
	}
	return `${minutes}:${String(rest).padStart(2, "0")}`;
}

const formatNumber = (value, language) =>
	new Intl.NumberFormat(language === "en" ? "en-GB" : "it-IT", { maximumFractionDigits: 1 }).format(value);

/** Una quantita' con la sua unita'. Le unita' non si sommano fra loro, quindi non si confondono nemmeno a video. */
export function formatQuantity(value, unit, language = "it") {
	if (value === null || value === undefined) return "—";
	const number = Number(value);
	switch (unit) {
		case "meters":
			return number >= 1000
				? `${formatNumber(number / 1000, language)} km`
				: `${formatNumber(number, language)} m`;
		case "seconds":
			return formatSeconds(number);
		case "calories":
			return `${formatNumber(number, language)} cal`;
		case "kg":
			return `${formatNumber(number, language)} kg`;
		case "reps":
		default:
			return `${formatNumber(number, language)} reps`;
	}
}

export const QUALITY_KEYS = {
	recorded: "monQualityRecorded",
	from_prescription: "monQualityFromPrescription",
	from_confirmation: "monQualityFromConfirmation",
	from_score: "monQualityFromScore",
	partial: "monQualityPartial",
	indeterminate: "monQualityIndeterminate",
};

export function qualityLabelKey(quality) {
	return QUALITY_KEYS[quality] || QUALITY_KEYS.indeterminate;
}

/** La qualita' prevalente di un movimento (quella con piu' esposizioni), per il badge della riga. */
export function dominantQuality(movement) {
	const entries = Object.entries(movement?.qualities || {});
	if (!entries.length) return "indeterminate";
	entries.sort((a, b) => b[1] - a[1]);
	return entries[0][0];
}

/** Le unita' di un movimento in una riga leggibile: «25 reps · 2.500 kg». */
export function movementQuantityLabel(movement, language = "it") {
	const parts = Object.entries(movement?.units || {}).map(([unit, bucket]) =>
		formatQuantity(bucket.value, unit, language),
	);
	if (movement?.tonnage_kg) parts.push(formatQuantity(movement.tonnage_kg, "kg", language));
	return parts.join(" · ") || "—";
}

/** Giorni consecutivi senza programmazione ne' attivita', compressi in una riga sola. */
export function collapseRestDays(days) {
	const out = [];
	let run = null;
	(days || []).forEach((day) => {
		if (day.kind === "rest") {
			if (run) {
				run.to = day.date;
				run.count += 1;
			} else {
				run = { kind: "rest_run", from: day.date, to: day.date, count: 1 };
				out.push(run);
			}
			return;
		}
		run = null;
		out.push(day);
	});
	return out;
}

/**
 * Gli avvisi del pannello «Avvisi», nell'ordine in cui il coach li legge. Il
 * server tiene predefiniti e intervalli (`GET /coach/monitoring/settings`);
 * qui solo quale frase li spiega. Ogni frase ha i segnaposto `{parametro}` che
 * diventano campi numerici.
 */
export const ALERT_FORM = [
	{ code: "missing_logs", titleKey: "monAlertMissingTitle", sentenceKey: "monAlertMissingSentence", explainKey: "monAlertMissingExplain" },
	{ code: "inactive", titleKey: "monAlertInactiveTitle", sentenceKey: "monAlertInactiveSentence", explainKey: "monAlertInactiveExplain" },
	{ code: "high_rpe", titleKey: "monAlertRpeTitle", sentenceKey: "monAlertRpeSentence", explainKey: "monAlertRpeExplain" },
	{ code: "wellness_low", titleKey: "monAlertWellnessLowTitle", sentenceKey: "monAlertWellnessLowSentence", explainKey: "monAlertWellnessLowExplain" },
	{ code: "wellness_gap", titleKey: "monAlertWellnessGapTitle", sentenceKey: "monAlertWellnessGapSentence", explainKey: "monAlertWellnessGapExplain" },
];

/** «Almeno {count} risultati con RPE ≥ {threshold}» → testo e campi in ordine. */
export function splitTemplate(template) {
	const parts = [];
	const pattern = /\{([a-z_]+)\}/g;
	let last = 0;
	let match;
	const text = String(template || "");
	while ((match = pattern.exec(text))) {
		if (match.index > last) parts.push({ type: "text", value: text.slice(last, match.index) });
		parts.push({ type: "param", name: match[1] });
		last = match.index + match[0].length;
	}
	if (last < text.length) parts.push({ type: "text", value: text.slice(last) });
	return parts;
}

/** Gli errori di una bozza di avvisi: `{"inactive.days": {min, max}}`. Vuoto = si puo' salvare. */
export function alertDraftErrors(draft, schema) {
	const errors = {};
	Object.entries(schema || {}).forEach(([code, params]) => {
		Object.entries(params || {}).forEach(([name, spec]) => {
			const raw = draft?.[code]?.[name];
			const value = typeof raw === "string" ? Number(raw.trim()) : raw;
			const valid =
				(typeof raw === "number" || (typeof raw === "string" && raw.trim() !== "")) &&
				Number.isInteger(value) &&
				value >= spec.min &&
				value <= spec.max;
			if (!valid) errors[`${code}.${name}`] = { min: spec.min, max: spec.max };
		});
	});
	return errors;
}

/** La bozza pronta per `PUT /coach/monitoring/settings`: numeri interi, niente stringhe. */
export function alertDraftPayload(draft, schema) {
	const alerts = {};
	Object.entries(schema || {}).forEach(([code, params]) => {
		alerts[code] = { enabled: draft?.[code]?.enabled !== false };
		Object.keys(params || {}).forEach((name) => {
			alerts[code][name] = Number(draft?.[code]?.[name]);
		});
	});
	return alerts;
}

/** Il coach ha cambiato qualcosa rispetto ai predefiniti? */
export function alertSettingsCustomized(alerts, defaults) {
	return Object.entries(defaults || {}).some(([code, values]) =>
		Object.entries(values || {}).some(([name, value]) => alerts?.[code]?.[name] !== value),
	);
}

/* ------------------------------------------------------------------ */
/* Ordinamento della panoramica                                         */
/* ------------------------------------------------------------------ */

export const SORT_KEYS = ["attention", "name", "last_activity", "coverage", "missing", "extra", "wellness", "flags"];

/**
 * L'ordine predefinito della panoramica: «chi devo guardare» (D32).
 *
 * Quattro fasce, poi dentro la fascia piu' avvisi gravi, piu' giorni senza
 * attivita', il nome:
 *   0. avvisi `warn` (giornate senza esito, inattivita', benessere basso) su
 *      un atleta che nei 90 giorni ha registrato qualcosa: e' un cambiamento;
 *   1. solo avvisi `info` (RPE alto, diario vuoto);
 *   2. nessun avviso;
 *   3. nessuna attivita' nei 90 giorni: un problema di adozione, non della
 *      settimana. Restano in elenco, in fondo, per non coprire gli altri ogni
 *      lunedi'.
 * Funziona anche con gli avvisi tutti spenti: resta l'inattivita'.
 */
export function attentionRank(row) {
	const flags = (row?.flags || []).filter((flag) => flag.code !== "no_data");
	const warn = flags.filter((flag) => flag.severity === "warn").length;
	const dormant = !row?.last_activity_date;
	let tier = 2;
	if (dormant) tier = 3;
	else if (warn) tier = 0;
	else if (flags.length) tier = 1;
	const days = dormant ? Number.MAX_SAFE_INTEGER : Number(row.days_since_activity || 0);
	return [tier, -warn, -flags.length, -days];
}

function compareAttention(a, b) {
	const ra = attentionRank(a);
	const rb = attentionRank(b);
	for (let index = 0; index < ra.length; index += 1) {
		if (ra[index] !== rb[index]) return ra[index] - rb[index];
	}
	return 0;
}

const sortValue = (row, key) => {
	switch (key) {
		case "last_activity":
			// Senza attivita' nota si va in fondo in entrambi i versi.
			return row.last_activity_date || null;
		case "coverage":
			return coverageRatio(row);
		case "missing":
			return Number(row.missing || 0);
		case "extra":
			return Number(row.extra_days || 0);
		case "wellness":
			return row.wellness?.enabled ? Number(row.wellness.entries || 0) : null;
		case "flags":
			return (row.flags || []).filter((flag) => flag.code !== "no_data").length;
		case "name":
		default:
			return `${row.name || ""} ${row.surname || ""}`.trim().toLowerCase();
	}
};

/** Le righe ordinate; i valori mancanti restano in fondo qualunque sia il verso. */
export function sortAthleteRows(rows, key = "name", direction = "asc") {
	const sign = direction === "desc" ? -1 : 1;
	if (key === "attention") {
		return [...(rows || [])].sort(
			(a, b) => sign * compareAttention(a, b) || (sortValue(a, "name") < sortValue(b, "name") ? -1 : 1),
		);
	}
	return [...(rows || [])].sort((a, b) => {
		const va = sortValue(a, key);
		const vb = sortValue(b, key);
		if (va === null || va === undefined) return vb === null || vb === undefined ? 0 : 1;
		if (vb === null || vb === undefined) return -1;
		if (va < vb) return -1 * sign;
		if (va > vb) return 1 * sign;
		return sortValue(a, "name") < sortValue(b, "name") ? -1 : 1;
	});
}

/* ------------------------------------------------------------------ */
/* Andamenti per esercizio                                              */
/* ------------------------------------------------------------------ */

export const TREND_WEEKS = [4, 8, 12, 26, 52];
export const TREND_BUCKETS = ["week", "session", "month", "program"];

/**
 * Le misure di un andamento. `planned` e' il campo del secchio che regge il
 * confronto con il programmato (solo serie e ripetizioni: il carico
 * programmato in percentuale non si somma). `unit` guida il formato.
 */
export const TREND_METRICS = [
	{ key: "reps", labelKey: "monMetricReps", unit: "reps", planned: "planned_reps" },
	{ key: "sets", labelKey: "monMetricSets", unit: "count", planned: "planned_sets" },
	{ key: "tonnage_kg", labelKey: "monMetricTonnage", unit: "kg" },
	{ key: "top_load_kg", labelKey: "monMetricTopLoad", unit: "kg" },
	{ key: "avg_load_kg", labelKey: "monMetricAvgLoad", unit: "kg" },
	{ key: "rpe_avg", labelKey: "monMetricRpe", unit: "rpe" },
	{ key: "sessions", labelKey: "monMetricSessions", unit: "count" },
	{ key: "meters", labelKey: "monMetricMeters", unit: "meters" },
	{ key: "seconds", labelKey: "monMetricSeconds", unit: "seconds" },
	{ key: "calories", labelKey: "monMetricCalories", unit: "calories" },
];

/** Le misure che hanno almeno un valore nei secchi: le altre non si offrono. */
export function availableTrendMetrics(buckets) {
	return TREND_METRICS.filter((metric) =>
		(buckets || []).some((bucket) => {
			const value = bucket?.[metric.key];
			return value !== null && value !== undefined && value !== 0;
		}),
	);
}

export function formatMetricValue(value, unit, language = "it") {
	if (value === null || value === undefined) return "—";
	if (Array.isArray(value)) {
		// Un intervallo (min–max), come lo portano le barre dell'intensita'.
		const [low, high] = value;
		if (low === high) return formatMetricValue(low, unit, language);
		return `${formatMetricValue(low, unit, language)} – ${formatMetricValue(high, unit, language)}`;
	}
	if (unit === "count") return String(value);
	if (unit === "rpe") return String(value);
	if (unit === "percent") return `${Math.round(Number(value) * 10) / 10}%`;
	return formatQuantity(value, unit, language);
}

/** L'etichetta breve di un secchio sull'asse: «13/10», «sett. 13/10», «ott 26», nome del programma. */
export function trendBucketLabel(bucket, granularity, language = "it") {
	if (granularity === "program") return bucket.label || "—";
	if (granularity === "month") {
		const [year, month] = String(bucket.key).split("-");
		return new Date(Date.UTC(Number(year), Number(month) - 1, 1)).toLocaleDateString(
			language === "en" ? "en-GB" : "it-IT",
			{ month: "short", year: "2-digit", timeZone: "UTC" },
		);
	}
	return formatShortDay(bucket.key, language);
}

/** I punti del grafico: valore della misura e, se c'e', il programmato dello stesso secchio. */
export function trendChartData(buckets, metric, granularity, language = "it") {
	return (buckets || []).map((bucket) => ({
		key: bucket.key,
		label: trendBucketLabel(bucket, granularity, language),
		value: bucket[metric.key] ?? null,
		planned: metric.planned ? (bucket[metric.planned] ?? null) : null,
		from: bucket.from,
		to: bucket.to,
	}));
}

/** C'e' un programmato da confrontare? Solo se almeno un secchio lo porta. */
export function hasPlannedSeries(data) {
	return (data || []).some((point) => point.planned !== null && point.planned !== undefined && point.planned !== 0);
}

// ---------------------------------------------------------------------------
// Famiglie di alzate, grafici piccoli, bande di programma e mesociclo, tabella
// compatta (D27, D28, D29 di docs/monitoring/DECISIONS.md)
// ---------------------------------------------------------------------------

/** Le famiglie di alzate del server (`services/movement_families.py`): etichetta tradotta. */
export const FAMILY_LABEL_KEYS = { snatch: "monFamilySnatch", clean_jerk: "monFamilyCleanJerk" };

export function familyLabel(family, t) {
	const key = FAMILY_LABEL_KEYS[family?.family_id];
	return key ? t(key) : family?.label || "";
}

/**
 * L'intervallo di calendario di un secchio: la settimana intera per `week`,
 * il mese per `month`, i giorni toccati altrimenti. Serve ad abbinare i
 * mesocicli, che sono intervalli di date, ai secchi del grafico.
 */
export function bucketCalendarRange(bucket, granularity) {
	const key = String(bucket?.key || "");
	if (granularity === "week" && /^\d{4}-\d{2}-\d{2}$/.test(key)) {
		return { start: key, end: shiftIsoDate(key, 6) };
	}
	if (granularity === "month" && /^\d{4}-\d{2}$/.test(key)) {
		const [year, month] = key.split("-").map(Number);
		const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
		return { start: `${key}-01`, end: `${key}-${String(last).padStart(2, "0")}` };
	}
	if (bucket?.from && bucket?.to) return { start: bucket.from, end: bucket.to };
	return null;
}

/**
 * Tratti contigui di secchi con lo stesso elemento, come i segmenti della
 * striscia stagionale del builder: `{ id, startIndex, span, item }`. Un
 * secchio senza elemento (`null`) interrompe il tratto.
 */
export function bandSegments(buckets, itemOf) {
	const segments = [];
	let current = null;
	(buckets || []).forEach((bucket, index) => {
		const item = itemOf(bucket, index);
		const id = item?.id;
		if (id === null || id === undefined) {
			current = null;
			return;
		}
		if (current && current.id === id) {
			current.span += 1;
			return;
		}
		current = { id, startIndex: index, span: 1, item };
		segments.push(current);
	});
	return segments;
}

/** Le bande dei programmi: il programma dominante di ogni secchio (`program_ids[0]`), col titolo. */
export function programBands(buckets, programs, granularity) {
	if (granularity === "program") return [];
	const titles = new Map((programs || []).map((program) => [program.program_id, program.title]));
	return bandSegments(buckets, (bucket) => {
		const id = bucket?.program_ids?.[0];
		return id ? { id, label: titles.get(id) || "" } : null;
	});
}

/**
 * Le bande dei mesocicli: la fase primaria di ogni secchio, scelta come nel
 * builder (la piu' corta, a parita' la piu' recente: `primaryMesocycle`).
 */
export function phaseBands(buckets, phases, granularity) {
	if (granularity === "program" || !phases?.length) return [];
	return bandSegments(buckets, (bucket) => {
		const primary = primaryMesocycle(mesocyclesForRange(phases, bucketCalendarRange(bucket, granularity)));
		return primary ? { id: primary.mesocycle_id, label: primary.name, color: primary.color, phase: primary } : null;
	});
}

const hasValue = (value) => value !== null && value !== undefined && value !== 0;

/**
 * I pannelli dei grafici piccoli, uno sotto l'altro sullo stesso asse del
 * tempo. Due misure con scale diverse non stanno mai sullo stesso asse: ogni
 * misura ha il suo pannello. Ogni serie dice il campo del secchio, il tipo
 * (`bar`, `line`, `range` = barra fluttuante min–max) e il ruolo del colore
 * (`main` eseguito, `muted` programmato, `second` misura di contorno).
 */
export const TREND_PANELS = [
	{ key: "sets", labelKey: "monMetricSets", unit: "count", series: [
		{ field: "sets", labelKey: "monDone", role: "main", kind: "bar" },
		{ field: "planned_sets", labelKey: "monPlanned", role: "muted", kind: "bar" },
	] },
	{ key: "reps", labelKey: "monMetricReps", unit: "reps", series: [
		{ field: "reps", labelKey: "monDone", role: "main", kind: "bar" },
		{ field: "planned_reps", labelKey: "monPlanned", role: "muted", kind: "bar" },
	] },
	{ key: "tonnage", labelKey: "monMetricTonnage", unit: "kg", series: [
		{ field: "tonnage_kg", labelKey: "monMetricTonnage", role: "main", kind: "bar" },
	] },
	{ key: "load", labelKey: "monChartLoad", unit: "kg", series: [
		{ field: "top_load_kg", labelKey: "monLoadTop", role: "main", kind: "bar" },
		{ field: "avg_load_kg", labelKey: "monLoadAvg", role: "second", kind: "line" },
	] },
	{ key: "intensity", labelKey: "monMetricIntensity", unit: "percent", series: [
		{ field: "intensity_range", labelKey: "monIntensityRealized", role: "main", kind: "range", from: "intensity_min", to: "intensity_max" },
		{ field: "intensity_avg", labelKey: "monIntensityRealizedAvg", role: "main", kind: "line", legend: false },
		{ field: "planned_pct_range", labelKey: "monIntensityPlanned", role: "muted", kind: "range", from: "planned_pct_min", to: "planned_pct_max" },
		{ field: "planned_pct_avg", labelKey: "monIntensityPlannedAvg", role: "muted", kind: "line", legend: false },
	] },
	{ key: "rpe", labelKey: "monMetricRpe", unit: "rpe", domain: [0, 10], series: [
		{ field: "rpe_avg", labelKey: "monMetricRpe", role: "main", kind: "bar" },
		{ field: "rpe_max", labelKey: "monRpeMax", role: "second", kind: "line" },
	] },
	{ key: "meters", labelKey: "monMetricMeters", unit: "meters", series: [
		{ field: "meters", labelKey: "monMetricMeters", role: "main", kind: "bar" },
	] },
	{ key: "seconds", labelKey: "monMetricSeconds", unit: "seconds", series: [
		{ field: "seconds", labelKey: "monMetricSeconds", role: "main", kind: "bar" },
	] },
	{ key: "calories", labelKey: "monMetricCalories", unit: "calories", series: [
		{ field: "calories", labelKey: "monMetricCalories", role: "main", kind: "bar" },
	] },
];

const seriesHasData = (series, buckets) =>
	(buckets || []).some((bucket) =>
		series.kind === "range" ? hasValue(bucket?.[series.from]) || hasValue(bucket?.[series.to]) : hasValue(bucket?.[series.field]),
	);

/**
 * I pannelli con almeno una serie valorizzata, ciascuno con le sole serie che
 * hanno dati. Il solo programmato basta a mostrare un pannello: «prescritto e
 * non fatto» e' un'informazione.
 */
export function availableTrendPanels(buckets) {
	return TREND_PANELS.map((panel) => {
		const series = panel.series.filter((item) => seriesHasData(item, buckets));
		return series.length ? { ...panel, series } : null;
	}).filter(Boolean);
}

// Quali misure si aprono per prime: per un'alzata volume e intensita', per la
// ginnastica ripetizioni e serie, per l'endurance distanza e tempo. Le altre
// restano a un clic («Altre misure»): un grafico per misura (D29) non vuol dire
// tutti aperti insieme.
const PANEL_PRIORITY = ["tonnage", "intensity", "load", "reps", "sets", "meters", "seconds", "calories", "rpe"];

/** I pannelli disponibili divisi in quelli aperti subito e gli altri, ognuno nell'ordine di `TREND_PANELS`. */
export function splitTrendPanels(panels, size = 3) {
	const list = panels || [];
	const chosen = new Set(
		[...list]
			.sort((a, b) => PANEL_PRIORITY.indexOf(a.key) - PANEL_PRIORITY.indexOf(b.key))
			.slice(0, size)
			.map((panel) => panel.key),
	);
	return {
		primary: list.filter((panel) => chosen.has(panel.key)),
		more: list.filter((panel) => !chosen.has(panel.key)),
	};
}

/** I punti di un pannello: etichetta del secchio e un campo per serie (gli intervalli come `[min, max]`). */
export function panelChartData(buckets, panel, granularity, language = "it") {
	return (buckets || []).map((bucket) => {
		const point = { key: bucket.key, label: trendBucketLabel(bucket, granularity, language), from: bucket.from, to: bucket.to };
		panel.series.forEach((series) => {
			if (series.kind === "range") {
				const low = bucket[series.from];
				const high = bucket[series.to];
				point[series.field] = low !== null && low !== undefined && high !== null && high !== undefined ? [low, high] : null;
			} else {
				point[series.field] = bucket[series.field] ?? null;
			}
		});
		return point;
	});
}

/**
 * Le colonne della tabella gemella, con intestazione breve (quella lunga nel
 * `title`). Si mostrano solo quelle con almeno un valore nel periodo: e' cosi'
 * che la tabella sta in una riga senza scorrere in orizzontale.
 */
export const TREND_TABLE_COLUMNS = [
	{ key: "sessions", shortKey: "monColSessionsShort", labelKey: "monMetricSessions", fields: ["sessions"] },
	{ key: "sets", shortKey: "monColSetsShort", labelKey: "monMetricSets", fields: ["sets", "planned_sets"] },
	{ key: "reps", shortKey: "monColRepsShort", labelKey: "monMetricReps", fields: ["reps", "planned_reps"] },
	{ key: "tonnage_kg", shortKey: "monColTonnageShort", labelKey: "monMetricTonnage", fields: ["tonnage_kg"] },
	{ key: "top_load_kg", shortKey: "monColTopLoadShort", labelKey: "monMetricTopLoad", fields: ["top_load_kg"] },
	{ key: "avg_load_kg", shortKey: "monColAvgLoadShort", labelKey: "monMetricAvgLoad", fields: ["avg_load_kg"] },
	{ key: "planned_pct", shortKey: "monColPlannedPctShort", labelKey: "monIntensityPlanned", fields: ["planned_pct_avg"] },
	{ key: "intensity", shortKey: "monColIntensityShort", labelKey: "monIntensityRealized", fields: ["intensity_avg"] },
	{ key: "rpe", shortKey: "monColRpeShort", labelKey: "monMetricRpe", fields: ["rpe_avg"] },
	{ key: "meters", shortKey: "monMetricMeters", labelKey: "monMetricMeters", fields: ["meters"] },
	{ key: "seconds", shortKey: "monMetricSeconds", labelKey: "monMetricSeconds", fields: ["seconds"] },
	{ key: "calories", shortKey: "monMetricCalories", labelKey: "monMetricCalories", fields: ["calories"] },
	{ key: "adherence", shortKey: "monColOutcomeShort", labelKey: "monTrendAdherence", fields: ["assigned_days"] },
];

export function visibleTrendColumns(buckets) {
	return TREND_TABLE_COLUMNS.filter((column) => (buckets || []).some((bucket) => column.fields.some((field) => hasValue(bucket?.[field]))));
}

/** «75% (70–80)» o «75%»: la media con l'intervallo quando gli estremi differiscono. */
export function formatPercentRange(avg, low, high) {
	if (avg === null || avg === undefined) return "—";
	const mid = `${Math.round(Number(avg) * 10) / 10}%`;
	if (low === null || low === undefined || high === null || high === undefined || low === high) return mid;
	return `${mid} (${Math.round(Number(low))}–${Math.round(Number(high))})`;
}

/** Un numero senza unita', per le celle di una tabella che porta l'unita' nell'intestazione. */
export function formatPlainNumber(value, language = "it") {
	if (value === null || value === undefined) return "—";
	return new Intl.NumberFormat(language === "en" ? "en-GB" : "it-IT", { maximumFractionDigits: 1 }).format(Number(value));
}
